import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, ClipboardList, FileSearch, ImagePlus, Loader2, Square, Volume2, X } from 'lucide-react';
import { shrinkPhoto } from '../../lib/claire/shrinkPhoto';
import { ClaireService } from '../../services/claireService';
import { CLAIRE_MODE_KEY, CLAIRE_MODES, savedClaireMode, type ClaireMessage, type ClaireMode, type ClaireSession } from '../../lib/claire/types';
import { ClaireText } from './ClaireText';
import { ClaireApprovalCard } from './ClaireApprovalCard';
import { FolderPhotosCard } from './FolderPhotosCard';
import { FolderListCard } from './FolderListCard';
import { ClaireGuideButton } from './ClaireGuideButton';
import { GiveClaireTaskDialog } from './GiveClaireTaskDialog';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import type { ClaireApproval } from '../../lib/claire/types';
import { cn } from '../../utils/cn';
import { onClaireAvatarError, useClaireAvatar } from '../../lib/claire/useClaireAvatar';

export const ClaireMark: React.FC<{ size?: number; className?: string }> = ({ size = 28, className }) => (
  <img
    src={useClaireAvatar()}
    onError={onClaireAvatarError}
    alt=""
    aria-hidden
    draggable={false}
    className={cn('inline-block rounded-full object-cover bg-[#241813] select-none shrink-0', className)}
    style={{ width: size, height: size }}
  />
);

const OUTCOME_LABEL: Record<string, string> = { blocked: 'Not permitted', approval: 'Waiting for approval', error: 'Did not complete' };

interface Props {
  sessionId: string | null;
  onSessionId: (id: string) => void;
  source: 'chat' | 'bubble';
  compact?: boolean;
  placeholder?: string;
  /** Text to place in the input (e.g. "Give Claire a Task"). Changing it refocuses the input. */
  prefill?: { text: string; nonce: number } | null;
  emptyState?: React.ReactNode;
  autoFocus?: boolean;
  /** Controlled mode; falls back to internal state when omitted. */
  mode?: ClaireMode;
  onModeChange?: (m: ClaireMode) => void;
}

const MAX_PHOTOS = 6;
/** What the server stores as the text of a photos-only message. */
const PHOTOS_ONLY_TEXT = 'Here are some photos.';

const PhotoStrip: React.FC<{ urls: string[]; size: number; onRemove?: (i: number) => void }> = ({ urls, size, onRemove }) => (
  <div className="flex flex-wrap gap-1.5">
    {urls.map((u, i) => (
      <div key={i} className="relative">
        <a href={onRemove ? undefined : u} target="_blank" rel="noreferrer">
          <img src={u} alt="" className="rounded-lg object-cover border border-black/10" style={{ width: size, height: size }} />
        </a>
        {onRemove && (
          <button
            type="button"
            onClick={() => onRemove(i)}
            aria-label="Remove photo"
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#241813] text-white flex items-center justify-center"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    ))}
  </div>
);

const MODE_PLACEHOLDER: Record<ClaireMode, string> = {
  ask: 'Ask Claire anything…',
  do: 'Tell Claire what to do…',
  watch: 'What should Claire keep an eye on?',
};

export const ClaireConversation: React.FC<Props> = ({
  sessionId,
  onSessionId,
  source,
  compact,
  placeholder,
  prefill,
  emptyState,
  autoFocus,
  mode: modeProp,
  onModeChange,
}) => {
  const [modeState, setModeState] = useState<ClaireMode>(savedClaireMode);
  const mode = modeProp ?? modeState;
  const setMode = (m: ClaireMode) => {
    setModeState(m);
    window.localStorage.setItem(CLAIRE_MODE_KEY, m);
    onModeChange?.(m);
  };
  const [speaking, setSpeaking] = useState<{ id: string; audio: HTMLAudioElement | null } | null>(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [messages, setMessages] = useState<ClaireMessage[]>([]);
  const [session, setSession] = useState<ClaireSession | null>(null);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState<{ key: number; text: string; images: string[] }[]>([]);
  const [photos, setPhotos] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [inFlight, setInFlight] = useState(0);
  const sending = inFlight > 0;
  const [error, setError] = useState<string | null>(null);
  const [approvals, setApprovals] = useState<ClaireApproval[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || '';
  const isMasterAdmin = !!currentUser?.roles?.includes(UserRole.MasterAdmin);

  useEffect(() => {
    setMessages([]);
    setSession(null);
    setApprovals([]);
    setError(null);
    if (!sessionId) return;
    const a = ClaireService.subscribeMessages(sessionId, setMessages);
    const b = ClaireService.subscribeSession(sessionId, setSession);
    const c = uid ? ClaireService.subscribeSessionApprovals(uid, sessionId, setApprovals) : () => undefined;
    return () => {
      a();
      b();
      c();
    };
  }, [sessionId, uid]);

  const approvalsByMessage = useMemo(() => {
    const assistant = messages.filter((m) => m.role === 'assistant' && !m.approvalId);
    const map = new Map<string, ClaireApproval[]>();
    for (const ap of approvals) {
      const at = ap.createdAt?.toMillis?.() || 0;
      const host = assistant.find((m) => (m.createdAt?.toMillis?.() || Infinity) >= at) || assistant[assistant.length - 1];
      if (!host) continue;
      map.set(host.id, [...(map.get(host.id) || []), ap]);
    }
    return map;
  }, [approvals, messages]);

  useEffect(() => {
    if (!prefill) return;
    setDraft(prefill.text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(prefill.text.length, prefill.text.length);
    });
  }, [prefill?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const list = [...messages];
    for (const p of pending) {
      if (messages.some((m) => m.role === 'user' && m.text === (p.text || PHOTOS_ONLY_TEXT) && !m.pending)) continue;
      list.push({ id: `__pending${p.key}`, role: 'user', text: p.text, images: p.images, pending: true });
    }
    return list;
  }, [messages, pending]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [shown.length, sending, approvals.length]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, compact ? 120 : 200)}px`;
  }, [draft, compact]);

  const addPhotos = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (!images.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) return setError(`Attach up to ${MAX_PHOTOS} photos at a time.`);
    try {
      const shrunk = await Promise.all(images.slice(0, room).map((f) => shrinkPhoto(f)));
      setPhotos((p) => [...p, ...shrunk].slice(0, MAX_PHOTOS));
      if (images.length > room) setError(`Only the first ${MAX_PHOTOS} photos were added.`);
      inputRef.current?.focus();
    } catch (e: any) {
      setError(e?.message || 'Couldn’t add that photo.');
    }
  };

  const send = () => sendMessage(draft.trim(), photos);

  const sendMessage = async (text: string, images: string[] = []) => {
    if (!text && !images.length) return;
    const id = sessionId || ClaireService.newSessionId();
    if (!sessionId) onSessionId(id);
    const key = Date.now() + Math.random();
    setDraft('');
    setPhotos([]);
    setPending((list) => [...list, { key, text, images }]);
    setInFlight((n) => n + 1);
    setError(null);
    try {
      await ClaireService.send(text, id, source, mode, images);
    } catch (e: any) {
      setError(e?.message?.replace(/^.*?:\s*/, '') || 'Claire could not be reached.');
      setDraft((d) => d || text);
      setPhotos((p) => (p.length ? p : images));
    } finally {
      setInFlight((n) => Math.max(0, n - 1));
      setPending((list) => list.filter((p) => p.key !== key));
    }
  };

  useEffect(() => () => speaking?.audio?.pause(), [speaking]);

  const listen = async (m: ClaireMessage) => {
    if (speaking?.id === m.id) {
      speaking.audio?.pause();
      setSpeaking(null);
      return;
    }
    speaking?.audio?.pause();
    setSpeaking({ id: m.id, audio: null });
    try {
      const audio = await ClaireService.speak(m.text);
      audio.onended = () => setSpeaking((s) => (s?.id === m.id ? null : s));
      setSpeaking((s) => (s?.id === m.id ? { id: m.id, audio } : (audio.pause(), s)));
    } catch (e: any) {
      setSpeaking(null);
      setError(e?.message?.replace(/^.*?:\s*/, '') || 'Claire’s voice is not available.');
    }
  };

  const status = sending ? session?.liveStatus || 'Claire is thinking…' : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div ref={scrollRef} className={cn('flex-1 min-h-0 overflow-y-auto', compact ? 'px-4 py-4 space-y-4' : 'px-1 py-6 space-y-6')}>
        {!shown.length && !sending && emptyState}
        {shown.map((m) =>
          m.role === 'user' ? (
            <div key={m.id} className="flex flex-col items-end gap-1.5">
              {!!m.images?.length && (
                <div className={cn('max-w-[85%] flex justify-end', m.pending && 'opacity-80')}>
                  <PhotoStrip urls={m.images} size={compact ? 64 : 88} />
                </div>
              )}
              {m.text && !(m.images?.length && m.text === PHOTOS_ONLY_TEXT) && (
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl rounded-br-md bg-[#241813] text-[#f8f3ea] px-4 py-2.5 whitespace-pre-wrap',
                    compact ? 'text-[13.5px]' : 'text-[15px]',
                    m.pending && 'opacity-80',
                  )}
                >
                  {m.text}
                </div>
              )}
            </div>
          ) : (
            <div key={m.id} className="flex gap-3">
              <ClaireMark size={compact ? 24 : 28} className="mt-0.5" />
              <div className="min-w-0 flex-1">
                <ClaireText text={m.text} className={cn('text-[#2b211c] leading-relaxed', compact ? 'text-[13.5px]' : 'text-[15px]')} />
                {m.researchId && (
                  <Link
                    to={`/claire/research/${m.researchId}`}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-[#e0d6c6] bg-white px-3 py-1.5 text-[12.5px] font-medium text-[#241813] hover:border-[#b8986e]"
                  >
                    <FileSearch className="w-4 h-4" /> View comps and full report
                  </Link>
                )}
                {m.folderJobId && <FolderPhotosCard jobId={m.folderJobId} compact={compact} onFollowUp={(t) => sendMessage(t)} />}
                {m.folderListJobId && <FolderListCard jobId={m.folderListJobId} compact={compact} onDone={(t) => sendMessage(t)} />}
                {!compact && m.text && (
                  <button
                    onClick={() => listen(m)}
                    title={speaking?.id === m.id ? 'Stop' : 'Listen'}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#a8988a] hover:text-[#241813]"
                  >
                    {speaking?.id === m.id ? (
                      speaking.audio ? <Square className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Volume2 className="w-3.5 h-3.5" />
                    )}
                    {speaking?.id === m.id ? 'Stop' : 'Listen'}
                  </button>
                )}
                {approvalsByMessage.get(m.id)?.map((ap) => (
                  <div key={ap.id} className="mt-2.5">
                    <ClaireApprovalCard approval={ap} isMasterAdmin={isMasterAdmin} compact={compact} />
                  </div>
                ))}
                {m.toolCalls?.some((t) => OUTCOME_LABEL[t.outcome] && t.outcome !== 'approval') && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.toolCalls
                      .filter((t) => OUTCOME_LABEL[t.outcome] && t.outcome !== 'approval')
                      .map((t, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                          {OUTCOME_LABEL[t.outcome]}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          ),
        )}
        {status && (
          <div className="flex gap-3 items-center">
            <ClaireMark size={compact ? 24 : 28} />
            <span className="flex items-center gap-2 text-[13px] text-[#8a7a6a]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              {status}
            </span>
          </div>
        )}
        {error && <p className="text-[13px] text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
      </div>

      <div className={cn('shrink-0', compact ? 'p-3 border-t border-[#efe7da]' : 'pt-3')}>
        <div className="flex items-center gap-1 mb-2" role="radiogroup" aria-label="Mode">
          {CLAIRE_MODES.map((m) => (
            <button
              key={m.id}
              role="radio"
              aria-checked={mode === m.id}
              title={m.hint}
              onClick={() => {
                setMode(m.id);
                inputRef.current?.focus();
              }}
              className={cn(
                'px-2.5 py-1 rounded-full text-[11.5px] font-medium border transition-colors',
                mode === m.id ? 'bg-[#241813] text-white border-[#241813]' : 'bg-white text-[#7a6a5a] border-[#e4dacb] hover:border-[#b8986e]',
              )}
            >
              {m.label}
            </button>
          ))}
          {!compact && <span className="ml-2 text-[11px] text-[#a8988a] truncate">{CLAIRE_MODES.find((m) => m.id === mode)?.hint}</span>}
          <button
            type="button"
            onClick={() => setTaskOpen(true)}
            title="Put a task on Claire’s schedule"
            className="ml-auto shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11.5px] font-medium border border-[#d9c7a8] bg-[#faf6ec] text-[#5a4430] hover:bg-[#f3ead8] transition-colors"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Give her a task
          </button>
          <ClaireGuideButton variant="icon" className="shrink-0" />
        </div>
        <GiveClaireTaskDialog open={taskOpen} onClose={() => setTaskOpen(false)} initialInstructions={draft} onAssigned={() => setDraft('')} />
        {photos.length > 0 && (
          <div className="mb-2 px-1">
            <PhotoStrip urls={photos} size={compact ? 52 : 64} onRemove={(i) => setPhotos((p) => p.filter((_, j) => j !== i))} />
          </div>
        )}
        <div className="flex items-end gap-2 rounded-2xl border border-[#e0d6c6] bg-white px-3 py-2 focus-within:border-[#b8986e] transition-colors shadow-sm">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              addPhotos(Array.from(e.target.files || []));
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={photos.length >= MAX_PHOTOS}
            title="Attach photos"
            aria-label="Attach photos"
            className="mb-0.5 w-8 h-8 rounded-full text-[#8a7a6a] flex items-center justify-center hover:bg-[#f6f1e9] hover:text-[#241813] disabled:opacity-30 transition-colors"
          >
            <ImagePlus className="w-[18px] h-[18px]" />
          </button>
          <textarea
            ref={inputRef}
            value={draft}
            autoFocus={autoFocus}
            onChange={(e) => setDraft(e.target.value)}
            onPaste={(e) => {
              const files = Array.from(e.clipboardData.files || []);
              if (files.some((f) => f.type.startsWith('image/'))) {
                e.preventDefault();
                addPhotos(files);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder={mode === 'ask' && placeholder ? placeholder : MODE_PLACEHOLDER[mode]}
            className={cn('flex-1 resize-none bg-transparent outline-none text-[#241813] placeholder:text-[#b3a594] py-1.5', compact ? 'text-[13.5px]' : 'text-[15px]')}
          />
          <button
            onClick={send}
            disabled={!draft.trim() && !photos.length}
            className="mb-0.5 w-8 h-8 rounded-full bg-[#241813] text-white flex items-center justify-center disabled:opacity-30 hover:bg-black transition-colors"
            aria-label="Send"
          >
            {sending && !draft.trim() && !photos.length ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};
